import { useEffect } from 'react';

import {
  MutationResult,
  QueryOptions,
  QueryParams,
  QueryResult,
  ResponseMeta,
  useMutation,
  useQuery,
} from '@betterment/js-runtime';

import { BillingPlan } from './types/billing_plans';
import { PortfolioAccount, PortfolioAccountMeta, PortfolioAccountQuery } from './types/portfolio_accounts';

import { ClientEnvelope, ClientEnvelopeMeta, SoloFourOhOneKPlan, User } from '../../models/ClientEnvelope';
import {
  AnonymousSecurityGroup,
  ListModelPortfoliosResponse,
  MarketAssumptionData,
  MigrateModelPortfolioRequest,
  ProjectionRequestData,
  RiskLevel,
  SavedModelPortfolio,
} from '../../models/CustomPortfolio';
import { WithoutKey } from '../../models/db';
import { Firm } from '../../models/firm';
import { ProjectionData } from '../../models/ProjectionGraph';

interface Solo401kParams {
  solo401KPlan: WithoutKey<SoloFourOhOneKPlan>;
}

export interface UpdateAdvisorParams {
  advisorId?: string;
  secondaryAdvisors?: string[];
}

interface ApiClientEnvelope {
  completedAt: string;
  createdAt: string;
  publishedAt: string;
  tables: ClientEnvelope;
  advisorId?: string;
  billingPlanId: string;
}

interface HouseholdSearchResult {
  name: string;
  id: string;
  members: string[];
}

export interface ClientSearchResponse {
  households: HouseholdSearchResult[];
  total: number;
}

export interface ClientSearchRequest {
  query: string;
  offset?: number;
  filter?: string;
}

export interface NotificationsCount {
  failedAcats: number;
  pendingAcats: number;
  missingBeneficiaries: number;
  accountLockouts: number;
  accountApprovals: number;
  pendingInvites: number;
  acatsApprovals: number;
  rmds: number;
}

export interface NotificationsCountResult {
  total: NotificationsCount;
  primary: NotificationsCount;
  secondary: NotificationsCount;
  unread: NotificationsCount;
}
export interface ApiPrimaryAdvisor {
  id: string;
  fullName: string;
  clientInvitePaperworkRequired: boolean;
  clientInviteAttestationRequirements?: string[];
}

export interface ApiSecondaryAdvisor {
  id: string;
  fullName: string;
  isAlreadySecondary: boolean;
}

export interface ClientEnvelopesSecondaryAdvisors {
  secondaryAdvisors: ApiSecondaryAdvisor[];
  showVisibilityWarning: boolean;
}

export interface UpdateSecondaryAdvisor {
  advisorId: string;
  key: number;
}

export interface TaxContext {
  grossIncome: number;
  maritalStatus: string;
  dependents: number;
  state: string;
  taxYear: number;
  itemizedDeductions: number;
  reportedFederalTaxBracketRatio: number;
  effectiveFederalTaxBracketRatio: number;
  taxFilingStatusDisplayName: string;
}

export type GoldmanSachsTaxSmartBondGuidanceBox = 'info' | 'warn' | 'error';
export interface ClientEnvelopeModelPortfolio extends Omit<
  SavedModelPortfolio,
  'status' | 'has401k' | 'assigned' | 'transferTargetName' | 'hasTransferInProgress' | 'incomingMigrationInProgress'
> {
  id: string;
  autoglideEligible: boolean;
  taxContext?: TaxContext;
  isGoldmanSachsTaxSmartBond?: boolean;
  goldmanSachsTaxSmartBondGuidanceBox?: GoldmanSachsTaxSmartBondGuidanceBox;
  driftCalculationLevel: 'security_group' | 'parent_group' | 'investment_type';
}

export interface DefaultClientEnvelopeModelPortfolio {
  id: string;
}

export interface Strategy {
  name: string;
  id: string;
}

export interface PortfolioStrategies {
  portfolioType: string;
  strategies: Strategy[];
}
export interface GoalPurpose {
  value: string;
  label: string;
}
export interface PortfolioResponseData {
  portfolio: ClientEnvelopeModelPortfolio;
  recommendedRiskLevelIndex: number;
}

export interface ApiInitialGoalData {
  defaultTermYears: number;
  defaultPurpose: string;
  portfolioStrategies: PortfolioStrategies[];
  accountPurposes: GoalPurpose[];
  defaultModelPortfolio: DefaultClientEnvelopeModelPortfolio;
  recommendedRiskLevelIndex: number;
  defaultPurposeIfUnselected: string;
}

interface ApiPortfolioProjectionsData {
  projectionData: ProjectionData;
}

interface APIPortfolioQueryProps {
  taxTreatment: string;
  termYears: number;
  adviceType: string;
}
export interface PublicationErrors {
  base?: string[];
}
export interface APIBillingPlanData {
  billingPlans: BillingPlan[];
  billingFrequency: string;
}
export interface ClientEnvelopeRequest extends ClientEnvelope {
  billingPlanId?: string;
  advisorId?: string;
}
export type ClientEnvelopeListUser = Pick<User, 'preferredName' | 'firstName' | 'lastName'>;
export type ClientEnvelopeListItem = {
  id: string;
  lastUpdated: string;
  users: ClientEnvelopeListUser[];
};

interface ClientsQuery {
  filter: string;
  query?: string;
  noLimit?: boolean;
}
export interface ClientResult {
  value: string;
  name: string;
  memberNames?: string;
  selected: boolean;
}

export interface FirmLogos {
  firm: {
    logoUrl: string;
    knockedOutLogoUrl: string;
  };
}

export interface PublishClientEnvelopeRequest {
  advisor_fee_bps?: number;
}

function useThrowMutationErrors<D, B>(
  mutation: MutationResult<D, B, ResponseMeta>
): MutationResult<D, B, ResponseMeta> {
  const [, { errors, networkError }] = mutation;
  useEffect(() => {
    if (errors) throw new Error(JSON.stringify(errors));
    if (networkError) throw new Error(JSON.stringify(networkError));
  }, [errors, networkError]);
  return mutation;
}

function useThrowQueryErrors<D>(query: QueryResult<D, ResponseMeta>): QueryResult<D, ResponseMeta> {
  const { errors, networkError } = query;
  useEffect(() => {
    if (errors) throw new Error(JSON.stringify(errors));
    if (networkError) throw new Error(JSON.stringify(networkError));
  }, [errors, networkError]);
  return query;
}

export function useGetNotificationsCounts(): QueryResult<NotificationsCountResult, ResponseMeta> {
  return useQuery<NotificationsCountResult, ResponseMeta>(`/api/v1/co_pilot/notifications`);
}

export function useGetClientEnvelopesDraftList(
  opts: QueryOptions = { offset: 0 } as QueryOptions
): QueryResult<ClientEnvelopeListItem[], ClientEnvelopeMeta> {
  return useQuery<ClientEnvelopeListItem[], ClientEnvelopeMeta>(`/api/v1/client_envelopes/drafts`, opts);
}

export function useGetClientEnvelope(
  id: string,
  opts: QueryOptions = {}
): QueryResult<ApiClientEnvelope, ResponseMeta> {
  return useThrowQueryErrors(useQuery<ApiClientEnvelope>(`/api/v1/client_envelopes/${id}`, opts));
}
export interface CreateClientEnvelopeResult {
  id: string;
}
export function useCreateClientEnvelope(): MutationResult<CreateClientEnvelopeResult, ClientEnvelope, ResponseMeta> {
  return useThrowMutationErrors(
    useMutation<CreateClientEnvelopeResult, ClientEnvelope>('/api/v1/client_envelopes', {
      method: 'post',
    })
  );
}

export const useSaveFourOhOneKPlanInAddAccount = (advisorGoalCreationFlowId: string) => {
  return useMutation<{ nextPath: string }, Solo401kParams>(
    `/api/v1/advisor_goal_creation_flows/${advisorGoalCreationFlowId}/solo_401k_plan`,
    {
      method: 'post',
    }
  );
};

export function useEmailValidation(): MutationResult<
  { valid: boolean },
  { email: string; clientEnvelopeId?: string; userKey?: number },
  {}
> {
  return useThrowMutationErrors(
    useMutation<{ valid: boolean }>(`/api/v1/client_envelopes/validations/email`, {
      method: 'post',
    })
  );
}

export const useSave401kStrategies = (advisorGoalCreationFlowId: string) => {
  return useMutation<{ nextPath: string }, { isOwner: boolean }>(
    `/api/v1/advisor_goal_creation_flows/${advisorGoalCreationFlowId}/solo_401k_strategies`,
    {
      method: 'post',
    }
  );
};

export const useGetContinuePath401kStrategies = (advisorGoalCreationFlowId: string) => {
  return useQuery<{ nextPath: string }>(
    `/api/v1/advisor_goal_creation_flows/${advisorGoalCreationFlowId}/solo_401k_strategies/new`
  );
};
export interface EINValidationResult {
  valid: boolean;
  messages: string[];
}

export function useEINValidation(): MutationResult<EINValidationResult, { ein: string }, {}> {
  return useMutation<EINValidationResult>(`/api/v1/client_envelopes/validations/ein`, {
    method: 'post',
  });
}

export function useUpdateClientEnvelope(
  id: string
): MutationResult<{ val: string }, ClientEnvelopeRequest, ResponseMeta> {
  return useThrowMutationErrors(
    useMutation<{ val: string }, ClientEnvelopeRequest>(`/api/v1/client_envelopes/${id}`, {
      method: 'put',
    })
  );
}
export function useDestroyClientEnvelope(id: string): MutationResult<{}, {}, ResponseMeta> {
  return useThrowMutationErrors(useMutation<{}, {}>(`/api/v1/client_envelopes/${id}`, { method: 'delete' }));
}

export function usePublishClientEnvelope(
  id: string
): MutationResult<{ errors?: { messages: PublicationErrors } }, PublishClientEnvelopeRequest, ResponseMeta> {
  return useMutation<{ errors?: { messages: PublicationErrors } }, PublishClientEnvelopeRequest>(
    `/api/v1/client_envelopes/${id}/publications`,
    {
      method: 'post',
    }
  );
}

export function useGetStrategyOptions(
  taxTreatment: string,
  dateOfBirth: string,
  accountType: string,
  opts: Omit<QueryOptions, 'qs'> = {}
): QueryResult<ApiInitialGoalData, ResponseMeta> {
  return useThrowQueryErrors(
    useQuery<ApiInitialGoalData>(`/api/v1/client_envelopes/accounts/strategies`, {
      ...opts,
      qs: { taxTreatment, dateOfBirth, accountType },
    })
  );
}

export function useGetPortfolioProjections(): MutationResult<ApiPortfolioProjectionsData, ProjectionRequestData> {
  return useThrowMutationErrors(
    useMutation<ApiPortfolioProjectionsData, ProjectionRequestData>('/api/v1/portfolios/projection_data', {
      method: 'post',
    })
  );
}

export function useGetPortfolio(
  id: string,
  qs: QueryParams
): MutationResult<PortfolioResponseData, APIPortfolioQueryProps> {
  return useThrowMutationErrors(
    useMutation<PortfolioResponseData, APIPortfolioQueryProps>(
      `/api/v1/client_envelopes/portfolios/model_portfolios/${id}`,
      {
        method: 'get',
        qs,
      }
    )
  );
}

export function useGetBillingPlans(opts: QueryOptions = {}): QueryResult<APIBillingPlanData, ResponseMeta> {
  return useThrowQueryErrors(useQuery<APIBillingPlanData>(`/api/v1/client_envelopes/firms/billing_plans`, opts));
}

export function useGetPortfolioAccounts({
  id,
  ...query
}: PortfolioAccountQuery): QueryResult<PortfolioAccount[], PortfolioAccountMeta> {
  return useQuery(`/api/v1/portfolios/model_portfolios/${id}/accounts`, {
    qs: query,
  });
}

export function useGetFirm(): QueryResult<Firm> {
  return useQuery('/api/v1/firm');
}

export function useGetFirmLogos(): QueryResult<FirmLogos> {
  return useQuery('/api/v1/firm/logos');
}

export function useGetModelPortfolio(id: string): QueryResult<SavedModelPortfolio> {
  return useQuery(`/api/v1/portfolios/model_portfolios/${id}`);
}

export function useGetModelPortfolios(): QueryResult<ListModelPortfoliosResponse> {
  return useQuery(`/api/v1/portfolios/model_portfolios`);
}

export function useGetBettermentAndPartnerPortfolios(): QueryResult<ListModelPortfoliosResponse> {
  return useQuery(`/api/v1/portfolios/betterment_and_partner_portfolios`);
}

export function useMigratePortfolio(sourcePortfolioId: string): MutationResult<{}, MigrateModelPortfolioRequest> {
  return useMutation(`/api/v1/portfolios/model_portfolios/${sourcePortfolioId}/transfers`);
}

export function useDestroyAssociatedPortfolioDraft(id: string): MutationResult<{}, {}> {
  return useMutation(`/api/v1/portfolios/associated_draft_portfolios/${id}`, {
    method: 'delete',
  });
}

export function useGetClients({ query, filter, noLimit }: ClientsQuery): QueryResult<ClientResult[], ClientsQuery> {
  return useQuery<ClientResult[], ClientsQuery>(`/api/v1/clients`, {
    qs: { query, filter, noLimit },
  });
}

export function useGetMarketAssumptions(): MutationResult<
  MarketAssumptionData[],
  { riskLevels: RiskLevel[]; anonymousSecurityGroups: AnonymousSecurityGroup[] }
> {
  return useMutation('/api/v1/portfolios/risk_level_cmas');
}
export function useGetPrimaryAdvisors(): QueryResult<ApiPrimaryAdvisor[]> {
  return useQuery<ApiPrimaryAdvisor[]>(`/api/v1/client_envelopes/primary_advisors`);
}

export function useGetSecondaryAdvisors(primaryAdvisorId: string): QueryResult<ClientEnvelopesSecondaryAdvisors> {
  return useQuery<ClientEnvelopesSecondaryAdvisors>(`/api/v1/client_envelopes/secondary_advisors/${primaryAdvisorId}`);
}
